import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray
} from '@angular/cdk/drag-drop';
import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatInput, MatPrefix, MatSuffix } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { InlineSvgComponent } from '../../../components/inline-svg/inline-svg.component';
import { ShareModalComponent } from '../../../components/share-modal/share-modal.component';
import {
  Project
} from '../../../models/project.module';
import { User } from '../../../models/user.model';
import { TruncatePipe } from '../../../pipes/truncate.pipe';
import { ProjectService } from '../../../services/project.service';
import { ToastNotificationService } from '../../../services/toast-notification.service';
import { CreateProjectModalComponent } from '../../risk-map/components/area-list/create-project-modal/create-project-modal.component';
import { NewProjectService } from '../../../services/newProject.service';
import { UserLogoBundleComponent } from '../../../components/user-logo-bundle/user-logo-bundle.component';

@Component({
  selector: 'frmg-project-list',
  templateUrl: './projectList.component.html',
  styleUrls: ['./projectList.component.scss'],
  imports: [
    MatTabNav,
    MatTabLink,
    MatTabNavPanel,
    MatFormField,
    InlineSvgComponent,
    MatPrefix,
    MatInput,
    ReactiveFormsModule,
    MatButton,
    MatTooltip,
    MatMenuTrigger,
    NgClass,
    MatMenu,
    MatMenuItem,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
    TruncatePipe,
    DatePipe,
    MatSuffix,
    UserLogoBundleComponent
  ]
})
export class ProjectListComponent implements OnInit {
  public projects: Project[] = [];
  public root: Project;
  public currentUser: User;
  public projectTypes: string[] = [
    'All',
    'My Projects',
    'Shared with me',
    'Archived'
  ];
  public activeType: string = this.projectTypes[1];
  public menuState: string;
  public currentSort: string = 'user_order_index:asc';

  public currentView: string = 'tile';
  public projectsSearchCtrl: FormControl = new FormControl<string>('');
  public loadingData: boolean = false;

  constructor(
    private projectService: ProjectService,
    private newProjectService: NewProjectService,
    private dialog: MatDialog,
    private router: Router,
    private toastNotificationService: ToastNotificationService,
    private cd: ChangeDetectorRef
  ) {
    this.currentView = localStorage.getItem('projectsViewType') || 'tile';
    this.currentSort = localStorage.getItem('projectsViewSort') || 'user_order_index:asc';
    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
    this.projectsSearchCtrl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((_search) => {
        this.getProjects();
      });
  }

  ngOnInit(): void {
    this.getProjects();
    this.getRootInfo();
  }

  public clearSearch() {
    this.projectsSearchCtrl.setValue('');
  }

  public getRootInfo() {
    this.projectService.getProjectsByPath('').subscribe((project) => {
      this.root = project;
    });
  }

  public changeView(type: string) {
    if (this.currentView === type) return;
    localStorage.setItem('projectsViewType', type);
    this.currentView = type;
  }

  public getProjects() {
    this.loadingData = true;
    this.projects = [];
    const queryKeys = {
      sorts: this.currentSort,
      filters: undefined
    };
    if (this.activeType == this.projectTypes[1] || this.activeType == this.projectTypes[2]) {
      queryKeys.filters = 'is_archived:eq:false';
    } else if (this.activeType === this.projectTypes[3]) {
      queryKeys.filters = 'is_archived:eq:true';
    }
    this.newProjectService
      .getProjectsManagement(queryKeys)
      .subscribe((projects) => {
        this.projects = projects.filter((project) => project.name.toLowerCase().includes(this.projectsSearchCtrl.value.toLowerCase()));
        if (this.activeType == this.projectTypes[1]) {
          this.projects = this.projects.filter((project) => project.is_owned_by_current_user);
        } else if (this.activeType === this.projectTypes[2]) {
          this.projects = this.projects.filter((project) => !project.is_owned_by_current_user);
        }
        this.loadingData = false;
      });
  }

  public goToProject(project: Project) {
    if (project.is_archived) return;
    this.router.navigate([`/projects/details/${project.id}`]);
  }

  public toggleMenu(state: Project, event?: MouseEvent) {
    event && event.stopPropagation();
    this.menuState = state?.id;
  }

  public changeType(type: string) {
    this.activeType = type;
    if (this.activeType === this.projectTypes[3]) {
      this.updateSortState('created_at:desc');
    } else this.getProjects();
  }

  public getProjectIconPath(project: Project) {
    if (project.is_owned_by_current_user && !project.is_archived) {
      return 'project.svg';
    } else if (project.is_archived) {
      return 'archived.svg';
    } else {
      return 'Shared.svg';
    }
  }

  public drop(event: CdkDragDrop<any>) {
    moveItemInArray(
      this.projects,
      event.previousContainer.data.index,
      event.container.data.index
    );
    if (this.currentSort === 'user_order_index:asc') {
      this.reorderServer(event.previousContainer.data.index, event.container.data.index);
    } else this.totalReorderServer();
  }

  public dropList(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.projects, event.previousIndex, event.currentIndex);
    this.reorderServer(event.previousIndex, event.currentIndex);
    if (this.currentSort === 'user_order_index:asc') {
      this.reorderServer(event.previousIndex, event.currentIndex);
    } else this.totalReorderServer();
  }

  public totalReorderServer() {
    this.currentSort = 'user_order_index:asc';
    localStorage.setItem('projectsViewSort', 'user_order_index:asc');
  }

  private reorderServer(currentIndex: number, prevIndex: number) {
    const index = prevIndex;
    const project = this.projects[index].id;
    let after_project_id = null;
    let before_project_id = null;
    if (index !== 0) {
      after_project_id = this.projects[index - 1].id;
    }
    if (index !== this.projects.length - 1) {
      before_project_id = this.projects[index + 1].id;
    }
    this.newProjectService.reorderProject(project, {
      after_project_id,
      before_project_id
    }).subscribe(() => {}, _ => {
      moveItemInArray(
        this.projects,
        currentIndex,
        prevIndex
      );
    });
  }

  public editProject(project: Project): void {
    const createProject = this.dialog.open(CreateProjectModalComponent, {
      data: {
        project
      }
    });
    createProject.afterClosed().subscribe((resp) => {
      if (resp) {
        const index = this.projects.findIndex(
          (project) => project.id === resp.id
        );
        this.projects[index] = resp;
      }

      this.cd.detectChanges();
    });
  }

  public createProject(): void {
    const createProject = this.dialog.open(CreateProjectModalComponent, {
      data: {
      }
    });
    createProject.afterClosed().subscribe((resp) => {
      if (resp) {
        this.getProjects();
      }
    });
  }

  public share(project: Project, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.dialog
      .open(ShareModalComponent<Project>, {
        data: {
          entity: project,
          canShare: project.is_owned_by_current_user
        }
      })
      .afterClosed()
      .subscribe((updated) => {
        if (updated) this.getProjects();
      });
  }

  public archiveProject(project: Project | Project, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: `Are you sure you want to ${
            !project.is_archived ? 'archive' : 'unarchive'
          } this project?`,
          body: !project.is_archived
            ? 'Archived projects can be restored anytime.'
            : 'Unarchived projects are saved in "My Projects".',
          confirmText: !project.is_archived ? 'Archive' : 'Unarchive'
        }
      })
      .afterClosed()
      .subscribe((del) => {
        if (del) {
          this.projectService.archiveProject(project).subscribe(() => {
            const index = this.projects.findIndex(
              (prj) => project.id === prj.id
            );
            if (index !== -1) {
              this.projects[index].is_archived =
                !this.projects[index].is_archived;
              this.cd.detectChanges();
              this.toastNotificationService.showNotification({
                type: 'success',
                message: `Project was successfully ${
                  this.projects[index].is_archived ? 'archived.' : 'unarchived.'
                }`,
                verticalPosition: 'top'
              });
              this.getProjects();
            }
          });
        }
      });
  }

  public unfollow(id: string): void {
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: 'Are you sure you want to remove this project from the list?',
          body: 'Permanently deleting this project will remove it for you and all users with access. All related data — uploads, downloads, and reports — will be permanently deleted and cannot be restored.',
          confirmText: 'Yes, remove project',
          cancelText: 'No'
        }
      })
      .afterClosed()
      .subscribe(() => {
        this.newProjectService.getShared().subscribe((resp) => {
          if (!resp) return;
          const shareToDelete =  resp.find(share => share.project_id === id);
          this.newProjectService.deleteShare(shareToDelete.id).subscribe(
            (_) => {
              this.toastNotificationService.showNotification({
                type: 'success',
                message: 'Project was successfully removed from your list.',
                verticalPosition: 'top'
              });
            }, error => {
              this.toastNotificationService.showNotification({
                type: 'error',
                message: error.error.detail,
                verticalPosition: 'top'
              });
            }
          );
        });
      });
  }

  public deleteProject(id: string, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: 'Are you sure you want to delete this project?',
          body: `Permanently deleting this project will remove it for you and all users with access. 
          All related data — uploads, downloads, and reports — will be permanently deleted and cannot be restored.`,
          confirmText: 'Delete'
        }
      })
      .afterClosed()
      .subscribe((del) => {
        if (del) {
          this.projectService.deleteProject(id).subscribe(() => {
            this.toastNotificationService.showNotification({
              type: 'success',
              message: 'Project deleted Successfully'
            });
            this.projects.forEach(() => {
              const index = this.projects.findIndex(
                (project) => project.id === id
              );
              if (index !== -1) this.projects.splice(index, 1);
            });
            this.cd.detectChanges();
          });
        }
      });
  }

  public updateSortState(state: string) {
    this.currentSort = state;
    localStorage.setItem('projectsViewSort', state);
    this.getProjects();
  }

  public hasMenuActions(item: Project): boolean {
    if (!item) return false;

    const canEdit =
      !item.is_archived &&
      (item.is_project ? item.is_owned_by_current_user : item.effective_permissions?.can_update);

    const canShare =
      !item.is_archived && item.is_owned_by_current_user;

    const canArchive =
      item.is_owned_by_current_user;

    const canDelete =
      !item.is_archived &&
      (item.is_project ? item.is_owned_by_current_user : item.effective_permissions?.can_delete);

    const canUnfollow =
      !item.is_archived && !item.is_owned_by_current_user;
    return canEdit || canShare || canArchive || canDelete || canUnfollow;
  }
}
